import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ShieldAlert, ListChecks, Gauge, Flame, TrendingUp, ArrowRight } from "lucide-react";
import { RadialBarChart, RadialBar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, Legend } from "recharts";
import { Link } from "react-router-dom";

const LEVELS = ["basso", "medio", "alto", "critico"];
const LEVEL_LABEL = { basso: "Basso", medio: "Medio", alto: "Alto", critico: "Critico" };

const cellClass = (count) => {
  if (count === 0) return "heat-empty";
  // We don't have a direct map per-cell, color by position handled below
  return "";
};

const heatCellColor = (impactIdx, probIdx) => {
  const sum = impactIdx + probIdx;
  if (sum <= 1) return "heat-low";
  if (sum <= 3) return "heat-medium";
  if (sum <= 4) return "heat-high";
  return "heat-critical";
};

export default function Dashboard() {
  const [k, setK] = useState(null);
  const [dueControls, setDueControls] = useState([]);

  useEffect(() => {
    api.get("/dashboard/kpis").then(({ data }) => setK(data));
    api.get("/dashboard/controls-due").then(({ data }) => setDueControls(data));
  }, []);

  if (!k) return <div className="text-slate-500">Caricamento KPI…</div>;

  const scoreColor = k.tcf_score >= 60 ? "#166534" : k.tcf_score >= 40 ? "#CA8A04" : "#991B1B";
  const scorePct = Math.max(0, Math.min(100, k.tcf_score)) / 100;

  const heatCount = (impact, prob) => k.heatmap?.[`${impact}|${prob}`] || 0;

  const processDist = Object.entries(k.distribution || {}).map(([code, v]) => ({
    code,
    Basso: v.basso || 0, Medio: v.medio || 0, Alto: v.alto || 0, Critico: v.critico || 0,
  }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Cruscotto TCF</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Dashboard</h1>
        </div>
        <Link to="/reports" className="text-sm text-slate-900 font-medium underline underline-offset-4" data-testid="generate-report-link">Genera report completo →</Link>
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <KpiCard testid="kpi-risks" icon={ShieldAlert} label="Rischi mappati" value={`${k.risks.present}`} sub={`su ${k.risks.total_available} disponibili`} progress={k.risks.total_available ? (k.risks.present / k.risks.total_available) * 100 : 0} />
        <KpiCard testid="kpi-controls" icon={ListChecks} label="Controlli attivi" value={`${k.controls.total}`} sub={
          <>
            <span className="text-red-700 mr-2">{k.controls.overdue} scaduti</span>
            <span className="text-amber-700">{k.controls.due_soon} in scadenza</span>
          </>
        } />
        <KpiCard testid="kpi-tcf-score" icon={Gauge} label="TCF Score" value={k.tcf_score} sub={`benchmark settore: ${k.industry_benchmark}`} score>
          <div className="mt-3 flex justify-center">
            <svg width="120" height="64" viewBox="0 0 120 64">
              <path d="M 10 60 A 50 50 0 0 1 110 60" stroke="#F1F5F9" strokeWidth="10" fill="none" strokeLinecap="round" />
              <path d="M 10 60 A 50 50 0 0 1 110 60" stroke={scoreColor} strokeWidth="10" fill="none" strokeLinecap="round" strokeDasharray={`${157 * scorePct} 200`} />
            </svg>
          </div>
        </KpiCard>
        <KpiCard testid="kpi-actions" icon={Flame} label="Azioni aperte" value={k.actions.open_total} sub={`${k.actions.open_high_priority} priorità alta`} />
      </div>

      {/* HEATMAP + CONTROLS DUE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-flat p-6" data-testid="heatmap-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-tag">Heat Map Rischi</div>
              <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">Impatto × Probabilità</h3>
            </div>
            <Link to="/risks" className="text-xs text-slate-500 hover:text-slate-900">Vedi tutti →</Link>
          </div>
          <div className="mt-6 grid grid-cols-[auto_repeat(4,1fr)] gap-1 text-xs">
            <div></div>
            {LEVELS.map((p) => <div key={p} className="text-center label-tag pb-2">{LEVEL_LABEL[p]}</div>)}
            {LEVELS.slice().reverse().map((impact) => (
              <Fragment key={`row-${impact}`}>
                <div className="label-tag flex items-center justify-end pr-2 text-right">{LEVEL_LABEL[impact]}</div>
                {LEVELS.map((prob) => {
                  const impactIdx = LEVELS.indexOf(impact);
                  const probIdx = LEVELS.indexOf(prob);
                  const count = heatCount(impact, prob);
                  return (
                    <div key={`${impact}-${prob}`} className={`p-4 text-center rounded-sm border border-slate-200 ${count > 0 ? heatCellColor(impactIdx, probIdx) : "heat-empty"}`} data-testid={`heat-${impact}-${prob}`}>
                      <div className="data-num text-2xl font-semibold">{count}</div>
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
          <div className="mt-4 text-xs text-slate-500">Asse Y: impatto · Asse X: probabilità</div>
        </Card>

        <Card className="card-flat p-6" data-testid="controls-due-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-tag">Prossime scadenze</div>
              <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">Controlli da eseguire</h3>
            </div>
            <Link to="/calendar" className="text-xs text-slate-500 hover:text-slate-900">Calendario →</Link>
          </div>
          <div className="mt-4 space-y-2">
            {dueControls.length === 0 && <div className="text-sm text-slate-500 py-10 text-center">Nessun controllo programmato</div>}
            {dueControls.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
                <div className="truncate flex-1">{c.description}</div>
                <div className="text-slate-500 text-xs ml-3 data-num">{c.next_due_at?.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Distribution per process */}
      <Card className="card-flat p-6" data-testid="process-distribution">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-tag">Distribuzione</div>
            <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">Rischi per processo</h3>
          </div>
        </div>
        <div className="mt-6 w-full" style={{ minWidth: 0 }}>
          {processDist.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">Nessun rischio mappato ancora. Vai alla <Link to="/risks" className="text-slate-900 underline">Mappa Rischi</Link>.</div>
          ) : (
            <ResponsiveContainer width="100%" aspect={3} minHeight={240}>
              <BarChart data={processDist}>
                <XAxis dataKey="code" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Basso" stackId="a" fill="#86EFAC" />
                <Bar dataKey="Medio" stackId="a" fill="#FDE68A" />
                <Bar dataKey="Alto" stackId="a" fill="#FDBA74" />
                <Bar dataKey="Critico" stackId="a" fill="#FCA5A5" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    </div>
  );
}

function KpiCard({ testid, icon: Icon, label, value, sub, children, score }) {
  return (
    <Card className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-tag">{label}</div>
        <Icon size={16} className="text-slate-400" strokeWidth={1.8} />
      </div>
      <div className={`mt-3 ${score ? "text-3xl" : "text-3xl"} data-num font-semibold text-slate-900`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
      {children}
    </Card>
  );
}
